/**
 * 메뉴가 "어느 대메뉴(CRM · IT-CONSULTING)의 어느 섹션"에 있는가 — 대메뉴를 건너 옮기기의 규칙.
 *
 * ★ 왜 따로 있나: 사이드바(applyMenuOverrides) · 관리 화면(buildAdminTree) · 권한 상속
 *   (MenuRoleContext) 세 곳이 같은 답을 내야 한다. 각자 계산하면 "관리 화면엔 IT 로 옮겨졌는데
 *   사이드바는 CRM 에 그대로" 같은 어긋남이 생긴다(menuSortOrder 와 같은 원칙).
 *
 * 규칙
 *  - 메뉴 키의 앞부분(crm: / automation:)은 "코드가 정의된 곳"일 뿐, 바뀌지 않는다.
 *    권한 키도 그대로라 menu_role_map 행을 옮길 필요가 없다.
 *  - 섹션(1depth)이 보이는 대메뉴 = 오버라이드 행의 area (없으면 키 앞부분).
 *  - 항목(2depth)은 area 를 따로 보지 않는다 — **부모 섹션이 있는 대메뉴**를 따른다.
 *    그래서 섹션을 옮기면 하위가 통째로 따라가고, 항목을 다른 대메뉴의 섹션에 넣으면 그쪽에 뜬다.
 *  - 문서 템플릿(crm:document-automation)은 전용 렌더러가 묶음을 쥐고 있어 대메뉴를 건너지 않는다.
 */
import { MENU_CODE_DEFAULTS, MENU_CODE_ORDER } from "./menuCodeDefaults";
import type { MenuOverride } from "./menuOverrides";

export type SidebarArea = "crm" | "automation";

export const SIDEBAR_AREAS: SidebarArea[] = ["crm", "automation"];

const isSidebarArea = (v: unknown): v is SidebarArea => v === "crm" || v === "automation";

/** 코드가 정의된 대메뉴(키 앞부분). 커스텀·설정 키는 null. */
export function homeAreaOf(menuKey: string): SidebarArea | null {
  const head = menuKey.split(":")[0];
  return isSidebarArea(head) ? head : null;
}

/** 대메뉴를 건너 옮길 수 있는가 — 문서 템플릿 묶음만 안 된다. */
export function canCrossArea(menuKey: string): boolean {
  return !menuKey.startsWith("crm:document-automation");
}

/** 코드 섹션 키(crm:sales …) — 사이드바 코드 순서 그대로. */
export const CODE_SECTION_KEYS = MENU_CODE_ORDER.filter((k) => k.split(":").length === 2);
/** 코드 항목 키(crm:sales:leads …). */
export const CODE_ITEM_KEYS = MENU_CODE_ORDER.filter((k) => k.split(":").length === 3);

export type MenuPlacement = {
  /** 섹션이 보이는 대메뉴. 섹션이 아니거나 없는 섹션이면 null. */
  sectionArea: (sectionKey: string) => SidebarArea | null;
  /** 항목의 부모 섹션 — 저장된 소속이 실제로 있는 섹션이면 그것, 아니면 코드 섹션. */
  itemParent: (itemKey: string) => string | null;
  /** 항목이 보이는 대메뉴 = 부모 섹션의 대메뉴. */
  itemArea: (itemKey: string) => SidebarArea | null;
};

/**
 * @param extraSectionKeys 호출하는 쪽 카탈로그에만 있는 섹션 키(테스트의 가짜 카탈로그 등).
 */
export function makeMenuPlacement(overrides: MenuOverride[], extraSectionKeys: Iterable<string> = []): MenuPlacement {
  // 키는 테이블에서 유일하다(onConflict menu_key) — area 로 거르지 않고 키로만 찾는다.
  const byKey = new Map(overrides.map((o) => [o.menu_key, o]));
  const codeSections = new Set([...CODE_SECTION_KEYS, ...Array.from(extraSectionKeys)]);

  const sectionArea = (sectionKey: string): SidebarArea | null => {
    const o = byKey.get(sectionKey);
    if (!codeSections.has(sectionKey)) {
      // 직접 추가한 섹션 — 부모 없는 커스텀 행만 섹션이다.
      return o?.is_custom && !o.parent_key && isSidebarArea(o.area) ? o.area : null;
    }
    const home = homeAreaOf(sectionKey);
    if (o && isSidebarArea(o.area) && canCrossArea(sectionKey)) return o.area;
    return home;
  };

  const itemParent = (itemKey: string): string | null => {
    const saved = byKey.get(itemKey)?.parent_key;
    if (saved && sectionArea(saved)) return saved;
    // 없어진 섹션을 가리키면 코드 섹션으로 — 메뉴가 사라지면 안 된다.
    const parts = itemKey.split(":");
    return parts.length >= 3 ? parts.slice(0, 2).join(":") : saved ?? null;
  };

  const itemArea = (itemKey: string): SidebarArea | null => {
    const parent = itemParent(itemKey);
    return parent ? sectionArea(parent) : null;
  };

  return { sectionArea, itemParent, itemArea };
}

export type PlacedMenuItem = {
  menuKey: string;
  area: SidebarArea;
  sectionKey: string;
  label: string;
  path: string;
  icon: string;
  pageTitle?: string;
  minRole?: string;
};

/**
 * 다른 대메뉴로 옮겨 간 코드 항목들 — 그 화면의 경로는 그대로라, 원래 레이아웃이 열린다.
 * 원래 레이아웃이 그 경로를 "모르는 화면"으로 보지 않게(권한 재검사·페이지 제목) 쓰는 목록.
 * @param resolveLabel 코드 라벨 → 사이드바 표시 라벨(CRM 은 번역 맵을 탄다)
 */
export function itemsMovedAwayFrom(
  area: SidebarArea, overrides: MenuOverride[], resolveLabel: (menuKey: string, fallback: string) => string = (_k, f) => f
): PlacedMenuItem[] {
  if (!overrides.length) return [];
  const placement = makeMenuPlacement(overrides);
  const byKey = new Map(overrides.map((o) => [o.menu_key, o]));
  const out: PlacedMenuItem[] = [];
  CODE_ITEM_KEYS.forEach((menuKey) => {
    if (homeAreaOf(menuKey) !== area) return;
    const placedArea = placement.itemArea(menuKey);
    if (!placedArea || placedArea === area) return;
    const code = MENU_CODE_DEFAULTS[menuKey] ?? {};
    const o = byKey.get(menuKey);
    const path = o?.path || code.path;
    if (!path) return;
    out.push({
      menuKey,
      area: placedArea,
      sectionKey: placement.itemParent(menuKey) ?? "",
      label: o?.label || resolveLabel(menuKey, code.label ?? menuKey.split(":").slice(-1)[0]),
      path,
      icon: o?.icon || code.icon || "",
      ...(o?.page_title ? { pageTitle: o.page_title } : {}),
      ...(code.minRole ? { minRole: code.minRole } : {}),
    });
  });
  return out;
}

/** 섹션의 표시 이름 — 오버라이드 > 코드 라벨(번역 적용) > 커스텀 행 이름. */
export function sectionLabelOf(
  sectionKey: string, overrides: MenuOverride[], resolveLabel: (menuKey: string, fallback: string) => string = (_k, f) => f
): string {
  const o = overrides.find((r) => r.menu_key === sectionKey);
  if (o?.label) return o.label;
  const code = MENU_CODE_DEFAULTS[sectionKey]?.label;
  return code ? resolveLabel(sectionKey, code) : sectionKey.split(":").slice(-1)[0];
}

/**
 * 이 경로를 어느 대메뉴의 틀(좌측 메뉴·상단 바)로 열어야 하나.
 *
 * ★ 화면 주소는 옮겨도 그대로라(/automation/… 은 IT 라우트), 주소만 보고 틀을 고르면
 *   CRM 으로 옮긴 IT 메뉴를 눌러도 좌측 메뉴가 IT 로 뜬다(사용자 신고). 그래서 틀은
 *   "이 경로의 메뉴가 지금 놓인 대메뉴"를 따른다. 메뉴에 없는 경로는 라우트의 대메뉴 그대로.
 * 가장 길게 맞는 메뉴 경로가 이긴다(사이드바 활성 표시와 같은 규칙). 길이가 같으면 라우트 쪽.
 */
export function frameAreaForPath(pathname: string, routeHome: SidebarArea, overrides: MenuOverride[]): SidebarArea {
  if (!overrides.length) return routeHome;
  const placement = makeMenuPlacement(overrides);
  const byKey = new Map(overrides.map((o) => [o.menu_key, o]));
  let best: { len: number; area: SidebarArea } | null = null;
  const consider = (path: string | null | undefined, area: SidebarArea | null) => {
    if (!path || !area || path === "/") return;
    if (pathname !== path && !pathname.startsWith(path + "/")) return;
    if (!best || path.length > best.len || (path.length === best.len && area === routeHome)) {
      best = { len: path.length, area };
    }
  };
  CODE_ITEM_KEYS.forEach((menuKey) => {
    consider(byKey.get(menuKey)?.path || MENU_CODE_DEFAULTS[menuKey]?.path, placement.itemArea(menuKey));
  });
  overrides.forEach((o) => {
    if (o.is_custom && o.parent_key) consider(o.path, placement.sectionArea(o.parent_key));
  });
  return (best as { len: number; area: SidebarArea } | null)?.area ?? routeHome;
}
